import i18n from 'i18n-js';
import moment from 'moment';
import { bytesToView, isWeb, localTime } from '@helpers';
import { scale as initialScale } from '@components/frame';
import { DATA_GRID_IMAGE_COLUMN_WIDTH, TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import { facetName } from '@components/search/components/mapper';
import { trackSearchResultTapped } from '@services/tracking';
import { HandleImagePressParams } from '@components/search/images/types';

export const handleImagePress = ({
  image,
  showActions,
  onItemSelect,
  onImagePress,
  handleImageNavigation,
}: HandleImagePressParams) => {
  trackSearchResultTapped('ImageDocument');

  if (onItemSelect) {
    onItemSelect(image);
    return;
  }

  const { documentId, contentHash } = image;

  if (onImagePress) {
    onImagePress({ contentHash });
    return;
  }

  handleImageNavigation({ documentId, showActions });
};

const makeDateColumn = (field: string, headerKey: string) => ({
  field,
  headerName: i18n.t(headerKey),
  align: 'left' as const,
  sortable: true,
  filterable: true,
  valueFormatter: (value: string | null) => (value ? moment(value).format('DD/MM/YYYY') : ''),
});

export const getDataGridColumns = (scale: (v: number) => number, facets?: any) => [
  {
    field: 'Image',
    headerName: '',
    align: 'center' as const,
    width: scale(DATA_GRID_IMAGE_COLUMN_WIDTH),
    sortable: false,
    filterable: false,
  },
  { field: 'fileName', headerName: i18n.t('File Name'), align: 'left' as const, flex: 1, minWidth: scale(200), sortable: true, filterable: true },
  { field: 'filePath', headerName: i18n.t('Original File Path'), align: 'left' as const, sortable: true, filterable: true },
  {
    field: 'fileSize',
    headerName: i18n.t('File Size'),
    align: 'left' as const,
    sortable: true,
    filterable: true,
    valueFormatter: (value: number | null) => (value ? bytesToView(value) : ''),
  },
  {
    field: 'tags',
    headerName: i18n.t('Type'),
    align: 'left' as const,
    type: 'singleSelect' as const,
    sortable: false,
    filterable: false,
    valueFormatter: (value: string[] | null) => value?.[0] ?? 'Other',
  },
  { field: 'extensionOriginal', headerName: i18n.t('Extension'), align: 'left' as const, sortable: true, filterable: true },
  makeDateColumn('createdOn', 'Date Created'),
  { field: 'createdBy', headerName: i18n.t('Created By'), align: 'left' as const, width: scale(200), sortable: true, filterable: true },
  makeDateColumn('modifiedOn', 'Date Modified'),
  { field: 'modifiedBy', headerName: i18n.t('Modified By'), align: 'left' as const, width: scale(200), sortable: true, filterable: true },
  {
    field: 'documentRetrievalStatus',
    headerName: i18n.t('Retrieval Status'),
    align: 'left' as const,
    type: 'singleSelect' as const,
    valueOptions: facets?.DocumentRetrievalStatus?.map((i: any) => ({ value: i?.value, label: facetName(i?.value) })),
    sortable: true,
    filterable: true,
  },
  { field: 'documentRetrievalStatusDetails', headerName: i18n.t('Retrieval Details'), align: 'left' as const, sortable: true },
  {
    field: 'pageCount',
    headerName: i18n.t('Total Pages'),
    align: 'left' as const,
    sortable: true,
    filterable: true,
    valueFormatter: (value: number | null) => value || '',
  },
  { field: 'source', headerName: i18n.t('Source'), align: 'left' as const, sortable: true, filterable: true },
  { field: 'sourceName', headerName: i18n.t('Source Name'), align: 'left' as const, sortable: true, filterable: true },
  {
    field: 'isCropped',
    headerName: i18n.t('Is Cropped'),
    align: 'left' as const,
    sortable: true,
    filterable: true,
    valueFormatter: (value: boolean) => i18n.t(value ? 'Yes' : 'No'),
  },
];

export const getItemStyle = (isEmpty: boolean, itemSize: number, isSmallMode: boolean, index: number, scale: (v: number) => number) => {
  const style: Record<string, any> = {
    opacity: isEmpty ? 0 : 1,
    marginBottom: (!isWeb && index === 0) ? 0 : scale(isSmallMode ? TILE_CONFIG.MARGIN_BOTTOM_SMALL : TILE_CONFIG.MARGIN_BOTTOM_NORMAL),
  };
  if (itemSize) {
    style.width = itemSize;
  }
  return style;
};

export const getImageSize = (itemSize: number, isLineMode: boolean, scale: (v: number) => number) => {
  if (isLineMode) {
    return { width: scale(TILE_CONFIG.LINE_MODE_WIDTH), height: scale(TILE_CONFIG.LINE_MODE_HEIGHT) };
  }
  const prop = itemSize / TILE_CONFIG.BASE_WIDTH;
  const newHeight = TILE_CONFIG.BASE_HEIGHT * prop;
  return { width: itemSize, height: newHeight };
};

export const buildImageData = (
  item: Record<string, any>,
  imgSize: { width: number; height: number },
  onPress: () => void,
  attachMode?: boolean,
) => ({
  image: {
    documentId: item.documentId ?? item?.id,
    imageHash: item.contentHash,
    imageData: {
      isCropped: item.isCropped ?? item?.cropped,
      extensionDerivative: item.extensionDerivative,
      pageNumber: item.pageNumber ?? item.page,
      rotationAngle: (item.imageDetails?.find((i: any) => i.pageNumber === item.pageNumber)?.rotationAngle ?? item?.customRotate?.[item?.page]) || 0,
      rotationAngleCrop: (item.imageCropSettings ?? item?.cropSettings)?.find((i: any) => i.pageNumber === item.pageNumber)?.rotationAngle || 0,
    },
    width: imgSize.width,
    height: imgSize.height,
    borderRadius: TILE_CONFIG.BORDER_RADIUS,
  },
  onPress,
  attachMode,
});

export const formatCreatedOn = (createdOn: string) => localTime(createdOn).format('DD MMMM YYYY - HH:mm');

export const buildTagsText = (links: any[]) => {
  const { TAG_NAMING, TAG_KEYS } = require('@components/imageHandling/common');
  return links.map((i: any) => `${i18n.t(TAG_NAMING[TAG_KEYS[i.scopeId]])} ${i.scopeReferenceName}`).join(', ');
};
